/**
 * Read and write .msg files — the game's text.
 *
 * The format, from `_message_load_field` (engine/src/message.cc): a record is
 * three brace-delimited fields in a row, `{num}{sfx}{text}`. The scanner skips
 * everything outside braces, so anything between records is a comment; a `}`
 * encountered while looking for the next `{` is a hard parse error in the
 * engine, which means comments must not contain braces. Newlines *inside* a
 * field are dropped, so a wrapped description is legal. `num` must parse as an
 * optionally-signed run of digits (`_message_parse_number`).
 *
 * The load-bearing discovery is in `_message_add`: inserting an entry whose
 * number already exists FREES the old text and overwrites it. Records are
 * consumed top to bottom, so **the last record for a number wins**. That makes
 * an edit an append: to rename an item we leave every original byte alone and
 * add `{1100}{}{Big Iron}` at the end of the file. The base file stays
 * byte-identical, the whole mod is legible as a trailing block, and two mods
 * renaming two different items merge by concatenation.
 *
 * Encoding: .msg files are single-byte (the vanilla files are CP437/1252-ish and
 * the engine treats them as raw bytes). We read and write latin1 so that a
 * parse→serialize round-trip is byte-exact for text we did not touch; authored
 * text is restricted to printable ASCII by `validate()`, because a recipe that
 * silently mojibakes a French install is worse than one that refuses.
 */

export class MsgError extends Error {}

export interface MsgRecord {
  num: number;
  sfx: string;
  text: string;
  /** Byte offset of the record's opening `{`. */
  start: number;
  /** Byte offset just past the record's closing `}`. */
  end: number;
}

export interface MsgFile {
  bytes: Uint8Array;
  records: MsgRecord[];
  /** num -> index of the *last* record with that number: the one the engine keeps. */
  byNum: Map<number, number>;
  /** Line ending the file predominantly uses, reused when appending. */
  eol: '\r\n' | '\n';
}

export function decodeLatin1(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return s;
}

export function encodeLatin1(text: string): Uint8Array {
  const out = new Uint8Array(text.length);
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    if (c > 0xff) throw new MsgError(`Character U+${c.toString(16)} cannot be written to a .msg file (single-byte encoding).`);
    out[i] = c;
  }
  return out;
}

/** Mirrors _message_load_field: find `{`, read to `}`, dropping newlines. */
function readField(src: string, from: number): { value: string; next: number } | null {
  let i = from;
  while (i < src.length && src[i] !== '{') {
    if (src[i] === '}') {
      throw new MsgError(`Mismatched delimiters at byte ${i}: a '}' appeared where a field was expected.`);
    }
    i++;
  }
  if (i >= src.length) return null;
  i++; // past '{'
  let value = '';
  while (i < src.length) {
    const ch = src[i];
    if (ch === '}') return { value, next: i + 1 };
    if (ch !== '\n') value += ch;
    i++;
  }
  throw new MsgError('Unterminated field: end of file reached inside a { }.');
}

export function parseMsg(bytes: Uint8Array): MsgFile {
  const src = decodeLatin1(bytes);
  const records: MsgRecord[] = [];
  const byNum = new Map<number, number>();

  let pos = 0;
  while (pos < src.length) {
    const numStart = src.indexOf('{', pos);
    if (numStart === -1) break;
    const num = readField(src, pos);
    if (!num) break;
    const sfx = readField(src, num.next);
    if (!sfx) throw new MsgError(`Record starting at byte ${numStart} has no sfx field.`);
    const text = readField(src, sfx.next);
    if (!text) throw new MsgError(`Record starting at byte ${numStart} has no text field.`);

    const parsed = parseNumber(num.value);
    if (parsed === null) {
      throw new MsgError(`Record at byte ${numStart} has a non-numeric number field ${JSON.stringify(num.value)}.`);
    }

    byNum.set(parsed, records.length);
    records.push({ num: parsed, sfx: sfx.value, text: text.value, start: numStart, end: text.next });
    pos = text.next;
  }

  const crlf = (src.match(/\r\n/g) ?? []).length;
  const lf = (src.match(/\n/g) ?? []).length;
  return { bytes, records, byNum, eol: crlf > 0 && crlf >= lf / 2 ? '\r\n' : '\n' };
}

function parseNumber(s: string): number | null {
  const t = s.trim();
  if (!/^[+-]?\d+$/.test(t)) return null;
  return parseInt(t, 10);
}

export function getEntry(msg: MsgFile, num: number): MsgRecord | null {
  const i = msg.byNum.get(num);
  return i === undefined ? null : msg.records[i];
}

export function getText(msg: MsgFile, num: number): string | null {
  return getEntry(msg, num)?.text ?? null;
}

export interface MsgEdit {
  num: number;
  text: string;
  sfx?: string;
}

/**
 * Appends override records for the given edits. Original bytes are untouched;
 * edits that would not change anything are dropped, so a no-op recipe compiles
 * to the base file byte-for-byte.
 */
export function applyMsgEdits(msg: MsgFile, edits: MsgEdit[]): Uint8Array {
  const effective = edits.filter((e) => {
    const cur = getEntry(msg, e.num);
    return !cur || cur.text !== e.text || (e.sfx !== undefined && cur.sfx !== e.sfx);
  });
  if (effective.length === 0) return msg.bytes;

  for (const e of effective) assertMsgSafe(e.text);
  for (const e of effective) if (e.sfx) assertMsgSafe(e.sfx);

  // De-duplicate: last edit for a number wins, matching the engine's own rule.
  const last = new Map<number, MsgEdit>();
  for (const e of effective) last.set(e.num, e);
  const ordered = [...last.values()].sort((a, b) => a.num - b.num);

  const eol = msg.eol;
  const tail = decodeLatin1(msg.bytes).endsWith('\n') ? '' : eol;
  const block =
    tail +
    `${eol}# --- rerun reactor overrides (last record for a number wins: message.cc _message_add) ---${eol}` +
    ordered.map((e) => `{${e.num}}{${e.sfx ?? ''}}{${e.text}}`).join(eol) +
    eol;

  const appended = encodeLatin1(block);
  const out = new Uint8Array(msg.bytes.length + appended.length);
  out.set(msg.bytes, 0);
  out.set(appended, msg.bytes.length);
  return out;
}

/** Text a .msg field can hold at all. Braces end the field; newlines vanish. */
export function assertMsgSafe(text: string): void {
  if (text.includes('{') || text.includes('}')) {
    throw new MsgError('Message text cannot contain { or }: those characters delimit fields and would truncate the entry.');
  }
  if (/[\r\n]/.test(text)) {
    throw new MsgError('Message text cannot contain line breaks: the loader strips them, so what you wrote is not what the game shows.');
  }
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    if (c < 0x20 || c > 0x7e) {
      throw new MsgError(
        `Message text must be printable ASCII in v1 (found U+${c.toString(16).padStart(4, '0')} at position ${i}). ` +
          'Non-English text needs a per-language target, which the format does not have yet.'
      );
    }
  }
}

/** Entry-level difference: what the edited file says that the original did not. */
export function diffMsg(before: MsgFile, after: MsgFile): MsgEdit[] {
  const out: MsgEdit[] = [];
  for (const [num, i] of after.byNum) {
    const a = after.records[i];
    const b = getEntry(before, num);
    if (!b || b.text !== a.text || b.sfx !== a.sfx) {
      out.push(a.sfx ? { num, text: a.text, sfx: a.sfx } : { num, text: a.text });
    }
  }
  return out.sort((x, y) => x.num - y.num);
}
